/**
 * @module app.shared
 */

import { autoinject, customAttribute, bindingMode } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { removeTextNodes, prependTextNode } from 'app/shared/lib/dom';

export interface IIcon {
    readonly library?: string | null;
    readonly symbol?: string | null;
    readonly ligature?: string | null;
}

export interface IIconAttribute {
    key: string | null;
    readonly icon: IIcon | null;
}

@autoinject()
@customAttribute('ess-icon', bindingMode.twoWay)
export class EssIconCustomAttribute implements IIconAttribute {

    public value: any;

    private currentKey: string | null = null;
    private currentIcon: IIcon | null = null;

    constructor(protected readonly element: Element, protected readonly i18n: I18N) { }

    protected attached() {
        this.element.classList.add('ess-icon');
    }

    protected detached() {
        this.element.classList.remove('ess-icon');
    }

    public get icon(): IIcon | null {
        return this.currentIcon;
    }

    public get key(): string | null {
        return this.currentKey;
    }

    public set key(value: string | null) {
        this.value = value;
    }

    protected valueChanged(newValue: any, oldValue: any): void {
        if(newValue === undefined || newValue === null) {
            this.updateIcon(null);
        }
        else if(typeof newValue === 'string') {
            this.updateIcon(newValue || null);
        }
        else {
            this.updateIcon(`${newValue}`);
        }
    }

    private updateIcon(key: string | null): void {
        this.currentKey = key;
        if(this.currentIcon) {
            this.clearIcon(this.currentIcon);
        }
        this.currentIcon = key ? this.lookupIcon(key) : null;
        if(this.currentIcon) {
            this.configureIcon(this.currentIcon);
        }
    }

    private lookupIcon(key: string): IIcon | null {
        const translation = <any>this.i18n.tr(key, { returnObjects: true });
        if(!translation || typeof translation === 'string') {
            return null;
        }

        return Object.freeze({ ...translation }) as IIcon;
    }

    private clearIcon(icon: IIcon): void {
        if(icon.ligature) { removeTextNodes(this.element); }
        if(icon.symbol) { this.element.classList.remove(...icon.symbol.split(/\s+/)); }
        if(icon.library) { this.element.classList.remove(icon.library); }
        this.element.classList.remove('ess-icon');
    }

    private configureIcon(icon: IIcon): void {
        this.element.classList.add('ess-icon');
        if(icon.library) { this.element.classList.add(icon.library); }
        if(icon.symbol) { this.element.classList.add(...icon.symbol.split(/\s+/)); }
        if(icon.ligature) { prependTextNode(this.element, icon.ligature); }
    }

}
